import { useEffect, useMemo, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { RoutesInterface } from "@/models";
import { isExactFeatureToggle, isExactRole } from "@/utils";
import { RoutesList } from "@/Routes";
import { Sidebar as BarteSidebar, MenuTitle, MenuLeaf, Submenu, Icon } from 'barte-design-system';
import {
    HouseIcon,
    PlusIcon,
    ChartLineIcon,
    ShoppingCartIcon,
    CreditCardIcon,
    MoneyIcon,
    WalletIcon,
    FileTextIcon,
    GearIcon,
    UsersIcon,
    ArrowsDownUpIcon,
    ReceiptIcon,
    IdentificationCardIcon,
    InvoiceIcon
} from '@phosphor-icons/react';

const iconMap: Record<string, any> = {
    "home_outline.svg": HouseIcon,
    "plus.svg": PlusIcon,
    "chart.svg": ChartLineIcon,
    "shopping_cart.svg": ShoppingCartIcon,
    "credit-card.svg": CreditCardIcon,
    "receive.svg": MoneyIcon,
    "receive-out.svg": ArrowsDownUpIcon,
    "subscription.svg": ReceiptIcon,
    "id.svg": IdentificationCardIcon,
    "settings.svg": GearIcon,
    "wallet.svg": WalletIcon,
    "report.svg": FileTextIcon,
    "users.svg": UsersIcon,
    "invoice.svg": InvoiceIcon,
};

const getPhosphorIcon = (svgIcon: string) => {
    return iconMap[svgIcon] || HouseIcon;
};

const Sidebar = () => {
    const { pathname } = useLocation();
    const navigate = useNavigate();
    const featureTogglesUser = JSON.parse(localStorage.getItem("barte-features-list-user"));
    const [openMenu, setOpenMenu] = useState<string | null>(null);
    const [routesApp, setRoutesApp] = useState<RoutesInterface>({
        HOME: [],
        ACQUIRING: [],
        BANKING: [],
        GENERAL: [],
    });
    const user = JSON.parse(localStorage.getItem("barte-user-info"));

    const exactMatchFeatureToggles = (item) => item.isShowMenu && isExactRole(user?.roles, item?.role) && isExactFeatureToggle(featureTogglesUser, item?.feature);

    const [acquiringPermissions, bankingPermissions] = useMemo(() => {
        const seller: string[] = [];
        const banking: string[] = [];

        for (const perm of user?.roles || []) {
            if (perm.startsWith("SELLER") && !seller.includes(perm)) {
                seller.push(perm);
            } else if (perm.startsWith("BANKING") && !banking.includes(perm)) {
                banking.push(perm);
            }
        }

        return [seller, banking];
    }, [user?.roles]);

    const getRoutesFiltered = (arr) => {
        return arr.reduce((acc, item) => {
            const newItem = item;
            if (item?.items) {
                newItem.items = getRoutesFiltered(item?.items);
            }
            if (exactMatchFeatureToggles(newItem)) {
                acc.push(newItem);
            }
            return acc;
        }, []);
    };

    useEffect(() => {
        let filteredRoutes = getRoutesFiltered(RoutesList);
        const grouped = filteredRoutes.reduce(
            (acc, route) => {
                const section = route.section || "GENERAL";
                if (!acc[section]) acc[section] = [];
                acc[section].push(route);
                return acc;
            },
            { HOME: [], ACQUIRING: [], BANKING: [], GENERAL: [] }
        );

        setRoutesApp(grouped);
    }, []);

    useEffect(() => {
        const allRoutes = [...routesApp.HOME, ...routesApp.ACQUIRING, ...routesApp.BANKING, ...routesApp.GENERAL];
        for (const route of allRoutes) {
            if (route.items?.some((item: any) => pathname.includes(item.path))) {
                setOpenMenu(route.title);
                break;
            }
        }
    }, [pathname, routesApp]);

    const handleMenuClick = (route: any) => {
        if (route.items?.length > 0) {
            setOpenMenu(openMenu === route.title ? null : route.title);
        } else {
            navigate(route.path);
        }
    };

    const renderMenuItem = (route: any) => {
        const { icon, path, title, items } = route;
        const hasSubmenu = items && items.length > 0;
        const isActive = hasSubmenu
            ? items.some((item: any) => pathname.includes(item.path))
            : pathname === path;
        const isOpen = openMenu === title;
        const PhosphorIcon = getPhosphorIcon(icon);

        if (hasSubmenu) {
            return (
                <MenuLeaf
                    key={title}
                    label={title}
                    icon={<Icon icon={PhosphorIcon} />}
                    isActive={isActive}
                    isOpen={isOpen}
                    onClick={() => handleMenuClick(route)}
                >
                    {items.map((sub: any) => (
                        <Submenu
                            key={sub.path}
                            label={sub.title}
                            isActive={pathname.includes(sub.path)}
                            onClick={() => navigate(sub.path)}
                        />
                    ))}
                </MenuLeaf>
            );
        }

        return (
            <MenuLeaf
                key={title}
                label={title}
                icon={<Icon icon={PhosphorIcon} />}
                isActive={isActive}
                onClick={() => handleMenuClick(route)}
            />
        );
    };

    const renderSection = (title: string, routes: any[], isDivider = false) => {
        if (!routes?.length) return null;

        const filteredRoutes = routes.filter((item) => item?.isShowMenu);
        if (!filteredRoutes.length) return null;

        return (
            <>
                {(title || isDivider) && (
                    <MenuTitle
                        title={title}
                        type={isDivider && !title ? "divider" : undefined}
                    />
                )}
                {filteredRoutes.map(renderMenuItem)}
            </>
        );
    };

    // Filtra "Configurações" do GENERAL (agora está no Header)
    const generalWithoutConfig = routesApp.GENERAL.filter(route => route.title !== "Configurações");

    return (

        <BarteSidebar>
            {renderSection("", routesApp.HOME)}
            {renderSection(
                bankingPermissions?.length > 0 ? "Adquirência" : "",
                routesApp.ACQUIRING
            )}
            {renderSection(
                acquiringPermissions?.length > 0 ? "Banking" : "",
                routesApp.BANKING
            )}
            {renderSection("", generalWithoutConfig, true)}
        </BarteSidebar>

    );
}

export { Sidebar };